import './style.css';

import { Loopable, loop_type, make_loop } from 'twrk/Loop.js';
import { css_manager } from 'twrk/Css.js';
import { pnts_manager } from 'twrk/Pnts.js';
import { plls_manager } from 'twrk/Plls.js';
import { lnks_manager } from 'twrk/Lnks.js';
import { jnts_manager } from 'twrk/Jnts.js';
import { hkks_manager } from 'twrk/Hkks.js';
import { Ease } from 'twrk/Ease.js';
import { Rapr2 } from 'twrk/Rapr2.js';
import { make_screen } from 'twrk/Scrn.js';
import { inpt_manager } from 'twrk/Inpt.js';
import { make_canvas } from 'twrk/Wbgl.js';
import { dbug_manager } from 'twrk/Dbug.js';

// import { lion_manager, lion_load } from './lion.js';
import { swinger_manager, swinger_load } from './swinger.js';
import { catcher_manager, catcher_load } from './catcher.js';
import { lights_manager } from './lights.js';
import { camera_shift, camera_land } from './camera.js';
import { background_load, background_make } from './background.js';
import { Toen, sound_load, crowd_cheer, crowd_ouch, music_start, show_start } from './sound.js';
import { twrk_type } from './types.js';
import { asset } from './asset.js';

async function App() {

    const Scrn = make_screen();
    Scrn.set_height(2.5);

    const Css = css_manager();

    const Pnts = pnts_manager();
    const Plls = plls_manager(48);
    const Lnks = lnks_manager(48);
    const Jnts = jnts_manager(8);
    const Hkks = hkks_manager();

    const Inpt = inpt_manager(Scrn, Pnts, Plls);

    await Rapr2.init();

    const Rapr = Rapr2.world({ gravity: 10 });

    const Canvas = make_canvas({ Scrn, Css, background: [0.1, 0.0, 0.3, 1] });

    //In Css mode make_canvas_fullscreen sizes the canvas once at creation and never again (no CSS
    //size, buffer + centering transform baked from the original window), so on resize/fullscreen it
    //stays small and off-center — the black border. Re-fit CSS size, buffer, viewport and centering
    //transform to the current viewport on every resize.
    const fit_canvas = () => {
        //size against the real screen in fullscreen: window.innerWidth/Height lag / report stale
        //values right after the transition, but screen.width/height are the true fullscreen size.
        const fs = !!document.fullscreenElement;
        const w = fs ? window.screen.width : window.innerWidth;
        const h = fs ? window.screen.height : window.innerHeight;
        const dpr = window.devicePixelRatio || 1;
        //CSS size pins the element to the viewport; buffer is that × dpr so it stays crisp on HiDPI.
        //Without an explicit CSS size the canvas renders at its buffer-px size and letterboxes.
        Canvas.canvas.style.width = w + "px";
        Canvas.canvas.style.height = h + "px";
        Canvas.canvas.width = Math.floor(w * dpr);
        Canvas.canvas.height = Math.floor(h * dpr);
        Canvas.canvas.style.transform = "translate(-" + w / 2 + "px, -" + h / 2 + "px)";
        Canvas.context.viewport(0, 0, Canvas.canvas.width, Canvas.canvas.height);
    };
    fit_canvas();
    Scrn.on_resize(fit_canvas);
    //Scrn already listens on window "resize" and calls its own resize() (which fires on_resize),
    //so plain window drags are covered — no extra listener needed here.

    const Dbug = dbug_manager(Scrn, Inpt);

    // await lion_load(Canvas);
    await background_load(Canvas, asset('background.png'));
    await background_load(Canvas, asset('intro.png'));
    await swinger_load(Canvas);
    await catcher_load(Canvas);
    await sound_load();

    const Background = background_make(Canvas, Scrn, asset('background.png'));
    //the intro splash runs through the same background renderer, so it gets the same crowd wiggle
    const Intro = background_make(Canvas, Scrn, asset('intro.png'));

    const twrk: twrk_type = { Canvas, Scrn, Inpt, Pnts, Plls, Lnks, Jnts, Hkks, Rapr, Dbug, Toen };

    // const Lion = lion_manager(twrk);
    // Lion.make({ x: 0.55, y: 0.35, size: 1.3 });

    const Swinger = swinger_manager(twrk);
    const swinger = Swinger.make({ x: -0, y: -0.25 });

    //two spotlights tracking the swinger from either side
    const Lights = lights_manager(twrk);
    Lights.make({ x: -2, y: 10 });
    Lights.make({ x: 2, y: 10 });

    const Catcher = catcher_manager(twrk);
    //each catcher's rope is shorter than the last: every caught goose adds its own body length to
    //the hanging chain, so shrinking ropes keep the whole unity from growing ever longer. Hooks
    //all sit at the same height, the geese just hang higher and higher.
    const catcher_hook_y = -3;
    const catcher_rope = (i: number) => Math.max(0.4, 2 - i * 0.5);
    const catchers = [0, 1, 2].map(i => {
        const rope = catcher_rope(i);
        return Catcher.make({ x: 4 + i * 4, y: catcher_hook_y + rope, rope });
    });

    //the catch: once the chain is free (rope let go, no catcher holding) and its free end drifts
    //within a catcher's tolerance (catch_tolerance in catcher presets), that catcher's beak holds
    //on and the camera pans right by the horizontal gap from wherever the chain last hung.
    //The free end starts as the swinger's hand; each catcher that joins and lets go of its own
    //rope contributes its front foot as the new free end, so the chain grows link by link.
    let catch_count = 0; //bed music starts on the third catch, then loops for the rest of the show
    let holder: ReturnType<typeof Catcher.make> | null = null;
    let chain_end = { hand: () => swinger.hand(), body: swinger.arm_upper, anchor: swinger.hand_anchor, released: () => swinger.is_released() };
    let last_x = swinger.hook.pos.x;
    const try_catch = () => {
        //only the entity currently trailing free (rope actually let go of) can be caught — not
        //just whether the swinger originally released, which stays true forever after the first drop
        if (holder || !chain_end.released()) return;
        const sh = chain_end.hand();
        for (const c of catchers) {
            if (c.holding()) continue; //already part of the chain, its beak is spoken for
            const ch = c.hand();
            if (Math.hypot(ch.x - sh.x, ch.y - sh.y) > c.tolerance) continue;
            c.hold(chain_end.body, chain_end.anchor);
            holder = c;
            crowd_cheer();
            catch_count++;
            if (catch_count === 3) music_start(show_end); //bed music kicks in on the third catch; its loop cues the end title
            camera_shift(c.hook.pos.x - last_x);
            camera_land();
            last_x = c.hook.pos.x;
            break;
        }
    };

    //station history: every catcher whose own rope has been released so far, in order — this is
    //"how far the swinger is". Falling replays the whole chain back to the moment of the *last*
    //release rather than starting over from the first rope. chain_end/last_x are snapshotted
    //alongside since they describe which link is currently the free end.
    const passed: ReturnType<typeof Catcher.make>[] = [];
    let chain_end_snapshot = chain_end;
    let last_x_snapshot = last_x;
    const take_chain_snapshot = () => {
        swinger.take_snapshot();
        for (const c of passed) c.take_snapshot();
        chain_end_snapshot = chain_end;
        last_x_snapshot = last_x;
    };

    //the show is over once the finale (end title) is up: all input is ignored from then on
    let finished = false;

    //space while held: the catcher lets go of its own rope — not of the caught hand — so catcher
    //and chain stay one unity, flying on together with the catcher's foot as the new free end
    //(the swinger's own space handler already spent itself on the initial rope release)
    Inpt.on_key(" ", () => {
        if (finished || !holder) return;
        holder.release();
        if (!passed.includes(holder)) passed.push(holder); //already there if this station was restored by a redo
        chain_end = { hand: holder.foot, body: holder.leg, anchor: holder.foot_anchor, released: holder.is_released };
        holder = null;
        take_chain_snapshot();
    });

    //redo: swinger fell past the floor — replay the whole chain built so far back to the moment
    //of the last release, instead of respawning from the very first rope. Any catcher that had
    //caught the chain end but not yet released its own rope (mid-swing, uncommitted) wasn't part
    //of the snapshot, so its hold is undone and it goes back to being un-caught.
    const redo_chain = () => {
        if (holder) { holder.unhold(); holder = null; }
        const last_station = passed[passed.length - 1];
        swinger.redo(!last_station); //only re-grip the swinger's own rope if no catcher is further down the chain
        for (const c of passed) c.redo(c === last_station);
        chain_end = chain_end_snapshot;
        last_x = last_x_snapshot;
        camera_land();
        //the last station is back to holding its own rope (re-gripped above) and still holding the
        //caught hand from the station before it — exactly the state try_catch leaves a catcher in,
        //so space needs to see it as `holder` again to let go of that rope on the next press
        holder = last_station ?? null;
    };

    //on-screen prompts to teach the controls: a CSS rect over the canvas, revealed on a timer.
    //Sized in world units and scaled by Scrn.res, matching Dbug's make_rect usage.
    const prompt = Css.make_rect({
        id: "prompt", parent: Scrn.dom, res: Scrn.res, passive: true, opacity: 0,
        x: 0, y: Scrn.y + 0.15, width: 0.25, height: 0.08, radius: 0.02,
        color: "rgb(0 0 0 / 55%)", text: "", text_color: "#fff", text_size: 0.04,
    });
    //keys the player has already used: a hint whose control is already discovered is pointless, so
    //show_prompt skips one whose target key is in here (checked when the prompt actually fires, so a
    //press during the delay still cancels it).
    const pressed = new Set<string>();
    let prompt_timer = 0;
    const show_prompt = (text: string, delay: number, key?: string) => {
        clearTimeout(prompt_timer); //a newer prompt cancels any still-pending one
        prompt_timer = window.setTimeout(() => {
            if (key && pressed.has(key)) return; //already figured out — don't teach it
            prompt.innerHTML = text;
            prompt.style.transition = "opacity 0.5s";
            prompt.style.opacity = "1";
        }, delay);
    };
    const hide_prompt = () => {
        clearTimeout(prompt_timer); //also cancel a prompt that hasn't faded in yet
        prompt.style.opacity = "0";
    };

    //end title: shown when the bed music's endless loop kicks in — the finale. A big centered
    //¡FELICIDADES! with a RESTART button under it that reloads the page (cleanest full reset of
    //physics/state; the click also re-arms the AudioContext at the intro gate).
    Css.make_rect({
        id: "title", parent: Scrn.dom, res: Scrn.res, passive: true, opacity: 0,
        x: 0, y: -0.1, width: 0.9, height: 0.2,
        text: "¡FELICIDADES!", text_color: "#fff", text_size: 0.12,
    });
    //pointer starts "none" so the hidden button doesn't swallow the intro click that starts the
    //show; show_end flips it to "auto" once the button is actually visible.
    const restart = Css.make_rect({
        id: "restart", parent: Scrn.dom, res: Scrn.res, opacity: 0, pointer: "none",
        x: 0, y: 0.25, width: 0.2, height: 0.09, radius: 0.02,
        color: "#fff", text: "RESTART", text_color: "#000", text_size: 0.045,
        cursor: "pointer", on_down: () => location.reload(),
    });
    const show_end = () => {
        finished = true;
        Swinger.finish(); //freeze input and stop restarting the drums for the finale
        for (const id of ["title", "restart"]) {
            const e = Css.get(id);
            if (!e) continue;
            e.style.transition = "opacity 0.6s";
            e.style.opacity = "1";
        }
        restart.style.pointerEvents = "auto"; //now clickable
    };

    //teach controls as the player uses them: Q hides the first hint; after 5 Q presses the crowd
    //has the hipp swing down, so cue the knees. Registers alongside swinger.ts's own "q" handler.
    let q_presses = 0;
    Inpt.on_key("q", () => {
        pressed.add("q");
        hide_prompt();
        q_presses++;
        if (q_presses === 5) show_prompt("W = KNEES", 0, "w");
    });
    let w_presses = 0;
    Inpt.on_key("w", () => {
        pressed.add("w");
        hide_prompt();
        w_presses++;
        if (w_presses === 3) show_prompt("E = ARMS", 0, "e");
    });
    let e_presses = 0;
    Inpt.on_key("e", () => {
        pressed.add("e");
        hide_prompt();
        e_presses++;
        if (e_presses === 2) show_prompt("SPACE = JUMP", 0, " ");
    });
    Inpt.on_key(" ", () => {
        pressed.add(" ");
        hide_prompt();
    });

    //intro gate: until the first click the show is frozen on the intro splash. The click both
    //unlocks the AudioContext (via Toen's own listener) and goes fullscreen, then starts the show.
    let started = false;
    window.addEventListener("click", () => {
        if (started) return;
        started = true;
        show_start();
        show_prompt("Q = LEGS", 8000, "q");
        document.documentElement.requestFullscreen?.().catch(() => { });
    });
    //re-fit when entering/leaving fullscreen (Scrn.resize fires fit_canvas via on_resize)
    document.addEventListener("fullscreenchange", () => Scrn.resize());

    let ouched = false; //latch so the fall's crowd ouch fires once per drop, re-armed after redo

    const Main: Loopable = {
        run: function (time: loop_type) {
            if (!started) {
                Canvas.clear();
                Intro.render(time.now / 1000);
                return;
            }
            Ease.run(time);
            Plls.run();
            Lnks.run();
            Jnts.run();
            Pnts.run(time);
            Rapr.step(time.delta_s);
            Canvas.clear();
            Background.render(time.now / 1000);
            Lights.run(swinger.torso.pos);
            // Lion.run();
            Swinger.run();
            Catcher.run();
            try_catch();
            //ouch the moment the fall is clearly a miss (torso past y 2), before the y>5 redo teleport,
            //so the crowd reacts as the llama drops rather than after it's already gone. Latched so it
            //fires once per fall; redo_chain re-arms it by pulling the torso back above the line.
            if (!ouched && swinger.torso.pos.y > 2) { crowd_ouch(); ouched = true; }
            if (swinger.fell()) { redo_chain(); ouched = false; }
            //Dbug.run no-ops when off, but the spread still builds a fresh array of every point/link
            //every frame — skip it entirely unless debug is actually showing
            Dbug.run(Scrn, Dbug.on ? [...Pnts.list, ...Plls.list, ...Lnks.list, ...Jnts.list, ...Hkks.list] : [], time);
        }
    };

    const time = make_loop();
    time.run([Main, Inpt]);
}

App();
